"use client";

import { differenceInCalendarDays, format, isMonday } from "date-fns";
import { GanttChartIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { EmptyState } from "../empty-state";
import { FOCUS_RING } from "../focus-ring";
import { HOVER_EASE } from "../states";
import { parseDay, type StatusTone } from "./task-meta";

// The timeline view (Work OS gantt-view.tsx geometry): a 224 px label column and a track per
// row 36 px tall (144 px labels on phones); a bar runs start → due (min 1.5 % wide), a due-only item is a rotated 12 px
// diamond; today is a 1 px --destructive line. Ticks follow the span: every day up to two
// weeks, every Monday up to ~two months, then each month's first. Bars are buttons named with
// their dates, so the chart reads without seeing it.
// WORK-OS: `dependencies` draws an elbow arrow from the end of the task depended on to the
// start of the one that waits (06 "Timeline (start/due, dependencies drawn)"), in an SVG laid
// over the rows; the track is measured so percentages and row pixels meet. Each row's button
// also says what it waits on.

export type TimelineItem = {
  id: string;
  title: string;
  /** YYYY-MM-DD; without it the item is a due-date diamond. */
  start?: string;
  /** YYYY-MM-DD. */
  end?: string;
  tone?: StatusTone;
  done?: boolean;
};

/** `to` waits on `from` (the arrow points at `to`). */
export type TimelineDependency = { from: string; to: string };

export type TimelineViewProps = Omit<React.ComponentProps<"section">, "children"> & {
  items: TimelineItem[];
  /** First and last day shown, YYYY-MM-DD (inclusive). */
  from: string;
  to: string;
  today?: string;
  dependencies?: TimelineDependency[];
  onItemOpen?: (id: string) => void;
  empty?: React.ReactNode;
};

const BAR_TONE: Record<StatusTone, string> = {
  neutral: "bg-border-strong",
  accent: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  info: "bg-info",
};

const HEADER_H = 32;
const ROW_H = 36;

export function TimelineView({
  items,
  from,
  to,
  today,
  dependencies = [],
  onItemOpen,
  empty,
  className,
  "aria-label": ariaLabel = "Timeline",
  ...props
}: TimelineViewProps) {
  const start = parseDay(from);
  const span = Math.max(1, differenceInCalendarDays(parseDay(to), start) + 1);
  // Percent of the track at the start of `day` (clamped).
  const pct = (day: string, endOfDay = false) =>
    Math.min(
      100,
      Math.max(
        0,
        ((differenceInCalendarDays(parseDay(day), start) + (endOfDay ? 1 : 0)) / span) * 100,
      ),
    );

  const ticks: { at: number; label: string; key: string }[] = [];
  for (let i = 0; i < span; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const show = span <= 14 ? true : span <= 62 ? isMonday(d) : d.getDate() === 1;
    if (show)
      ticks.push({
        at: (i / span) * 100,
        key: String(i),
        label: span <= 14 ? format(d, "EEE d") : span <= 62 ? format(d, "d MMM") : format(d, "MMM"),
      });
  }
  const todayPct = today ? pct(today) + 50 / span : null;
  const showToday = todayPct !== null && todayPct > 0 && todayPct < 100;

  const visible = items.filter((i) => i.end || i.start);
  const rowOf = new Map(visible.map((i, n) => [i.id, n]));
  const titleOf = new Map(visible.map((i) => [i.id, i.title]));
  const arrows = dependencies.filter((d) => rowOf.has(d.from) && rowOf.has(d.to));
  const waitsOn = new Map<string, string[]>();
  for (const d of arrows) waitsOn.set(d.to, [...(waitsOn.get(d.to) ?? []), d.from]);

  // The track in pixels, so arrows (px) and bars (%) agree; measured, so a resize re-draws.
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [track, setTrack] = React.useState<{ left: number; width: number } | null>(null);
  React.useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || !arrows.length) return;
    const measure = () => setTrack({ left: el.offsetLeft, width: el.offsetWidth });
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [arrows.length]);

  /** Where an item's bar begins and ends on the track, in percent. */
  const extent = (item: TimelineItem) => {
    const endDay = item.end ?? item.start ?? from;
    if (item.start) return { left: pct(item.start), right: pct(endDay, true) };
    const mid = pct(endDay) + 50 / span;
    return { left: mid, right: mid };
  };

  return (
    <section
      data-slot="timeline-view"
      aria-label={ariaLabel}
      className={cn("relative overflow-x-auto rounded-lg border border-border bg-card", className)}
      {...props}
    >
      <div
        className="relative min-w-[640px] [--tl-label:14rem] max-md:[--tl-label:9rem]"
        // Room for every tick label: 56 px a day on the day scale, then 14 and 4 px.
        style={{
          minWidth: `max(640px, calc(var(--tl-label) + ${span * (span <= 14 ? 56 : span <= 62 ? 14 : 4)}px))`,
        }}
      >
        <div className="flex h-8 border-b border-border bg-sunken">
          <div className="flex w-(--tl-label) shrink-0 items-center border-r border-border px-3 text-xs font-medium text-muted-foreground">
            Task
          </div>
          <div ref={trackRef} className="relative flex-1" aria-hidden={true}>
            {ticks.map((t) => (
              <span
                key={t.key}
                className="absolute inset-y-0 flex items-center border-l border-border-subtle pl-1.5 text-xs whitespace-nowrap text-muted-foreground tabular-nums"
                style={{ left: `${t.at}%` }}
              >
                {t.label}
              </span>
            ))}
          </div>
        </div>
        {visible.length === 0 ? (
          (empty ?? (
            <EmptyState
              variant="compact"
              icon={GanttChartIcon}
              title="Nothing scheduled in this range"
              description="Tasks with a start or due date show here."
            />
          ))
        ) : (
          <ul className="m-0 list-none p-0">
            {visible.map((item) => {
              const endDay = item.end ?? item.start ?? from;
              const waits = (waitsOn.get(item.id) ?? []).map((id) => titleOf.get(id) ?? id);
              const label = `${
                item.start
                  ? `${item.title}, ${format(parseDay(item.start), "d MMM")} to ${format(parseDay(endDay), "d MMM")}`
                  : `${item.title}, due ${format(parseDay(endDay), "d MMM")}`
              }${waits.length ? `; waits on ${waits.join(", ")}` : ""}`;
              const left = item.start ? pct(item.start) : pct(endDay) + 50 / span;
              const width = item.start ? Math.max(1.5, pct(endDay, true) - pct(item.start)) : 0;
              const tone = BAR_TONE[item.done ? "success" : (item.tone ?? "accent")];
              return (
                <li
                  key={item.id}
                  data-slot="timeline-view-row"
                  className="flex h-9 border-b border-border-subtle last:border-b-0 hover:bg-sunken"
                >
                  <div
                    className={cn(
                      "flex w-(--tl-label) shrink-0 items-center border-r border-border-subtle px-3 text-sm",
                      item.done ? "text-muted-foreground line-through" : "text-foreground",
                    )}
                  >
                    <span className="truncate">{item.title}</span>
                  </div>
                  <div className="relative flex-1">
                    <button
                      type="button"
                      aria-label={label}
                      title={label}
                      onClick={() => onItemOpen?.(item.id)}
                      className={cn(
                        "absolute",
                        item.start
                          ? "top-2 h-5 rounded-xs"
                          : "top-3 size-3 -translate-x-1/2 rotate-45 rounded-[2px]",
                        tone,
                        item.done && "opacity-60",
                        HOVER_EASE,
                        "hover:brightness-110",
                        FOCUS_RING,
                      )}
                      style={
                        item.start ? { left: `${left}%`, width: `${width}%` } : { left: `${left}%` }
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {arrows.length && track ? (
          <svg
            aria-hidden={true}
            data-slot="timeline-view-dependencies"
            className="pointer-events-none absolute top-0 left-0 z-10 text-faint"
            width={track.left + track.width}
            height={HEADER_H + visible.length * ROW_H}
          >
            <title>Dependencies</title>
            <defs>
              <marker
                id="tl-arrow"
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
              </marker>
            </defs>
            {arrows.map((d) => {
              const a = visible[rowOf.get(d.from) as number] as TimelineItem;
              const b = visible[rowOf.get(d.to) as number] as TimelineItem;
              const x1 = track.left + (extent(a).right / 100) * track.width;
              const x2 = track.left + (extent(b).left / 100) * track.width;
              const y1 = HEADER_H + (rowOf.get(d.from) as number) * ROW_H + ROW_H / 2;
              const y2 = HEADER_H + (rowOf.get(d.to) as number) * ROW_H + ROW_H / 2;
              // Elbow out of the predecessor, down (or up) to the dependent's row, then in; when
              // the dependent starts before the predecessor ends, route around the row edge.
              const path =
                x2 >= x1 + 16
                  ? `M${x1},${y1} H${x1 + 8} V${y2} H${x2}`
                  : `M${x1},${y1} H${x1 + 8} V${y1 + (y2 > y1 ? ROW_H / 2 : -ROW_H / 2)} H${x2 - 12} V${y2} H${x2}`;
              return (
                <path
                  key={`${d.from}-${d.to}`}
                  data-dependency={`${d.from}>${d.to}`}
                  d={path}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  markerEnd="url(#tl-arrow)"
                />
              );
            })}
          </svg>
        ) : null}
        {showToday ? (
          <div
            aria-hidden={true}
            data-slot="timeline-view-today"
            className="pointer-events-none absolute top-0 bottom-0 z-10 w-px bg-destructive"
            style={{
              left: `calc(var(--tl-label) + (100% - var(--tl-label)) * ${(todayPct ?? 0) / 100})`,
            }}
          />
        ) : null}
      </div>
    </section>
  );
}
